// Who reacted, on phones: press and hold a reaction to open this bottom sheet, like Discord's. A row of tabs, one per
// emoji on the message with its count, opened on the one you held; under it, everyone who left the selected emoji,
// with their avatar and name. Live: reactions coming and going while it's open update it, a tab whose emoji is gone
// falls back to the first, and the sheet closes when the message has no reactions left.
import { useEffect, useRef, useState } from "react";
import { Sheet } from "./Sheet.tsx";
import { JUMBO_MARK } from "@bloom/shared";
import { Avatar } from "./ui.tsx";
import { ReactionGlyph, reactionLabel, useServerEmoji } from "../customEmoji.tsx";

export function ReactionSheet({ reactions, jumbo, emoji, names, me, onClose }: { reactions: Record<string, string[]>; /** Who reacted jumbo, by emoji (#714): their row carries the size. */ jumbo?: Record<string, string[]>; emoji: string; names: Map<string, string>; me: string | null; onClose: () => void }) {
  const { byId } = useServerEmoji();
  const [picked, setPicked] = useState(emoji);
  const tabs = useRef<HTMLDivElement>(null);
  const entries = Object.entries(reactions).filter(([, users]) => users.length > 0);
  const selected = entries.find(([e]) => e === picked) ?? entries[0];
  const empty = entries.length === 0;

  useEffect(() => {
    if (empty) onClose();
  }, [empty]);
  // The emoji you held may be far along a long row of tabs: bring it into view.
  useEffect(() => {
    tabs.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ block: "nearest", inline: "center" });
  }, []);

  if (!selected) return null;
  const [current, users] = selected;
  return (
    <Sheet label="Reactions" onClose={onClose}>
      <div className="reactors">
        <div className="reactors-tabs" role="tablist" aria-label="Reactions" ref={tabs}>
          {entries.map(([e, who]) => (
            <button
              key={e}
              type="button"
              role="tab"
              className={`reactors-tab${e === current ? " on" : ""}`}
              aria-selected={e === current}
              aria-label={`${reactionLabel(e, byId)} ${who.length}`}
              onClick={() => setPicked(e)}
            >
              <span className="reactors-tab-emoji" aria-hidden="true"><ReactionGlyph emoji={e} /></span>
              <span aria-hidden="true">{who.length}</span>
            </button>
          ))}
        </div>
        <ul className="reactors-list sheet-scroll" role="tabpanel" aria-label={`Reacted with ${reactionLabel(current, byId)}`}>
          {users.map((u) => {
            const name = names.get(u) ?? "Someone";
            return (
              <li key={u} className="reactors-row">
                <Avatar id={u} name={name} size={32} />
                <span className="reactors-name">{name}</span>
                {jumbo?.[current]?.includes(u) && <span className="reactors-jumbo">{JUMBO_MARK}</span>}
                {u === me && <span className="reactors-you">You</span>}
              </li>
            );
          })}
        </ul>
      </div>
    </Sheet>
  );
}
